/**
 * Privacy — 개인정보처리방침 페이지 (/privacy)
 *
 * 브랜드: 인증이지 (CERT EASY)
 * 법적 개인정보처리자: 주식회사 메이커머스
 *
 * 구조: 메이커머스 컨펌받은 10조 구조 (안내서 PDF 17페이지 기준)
 *   1. 처리 목적
 *   2. 수집 항목 및 수집 방법
 *   3. 처리 및 보유 기간
 *   4. 제3자 제공
 *   5. 처리 위탁
 *   6. 정보주체의 권리·의무 및 행사 방법
 *   7. 개인정보의 파기
 *   8. 개인정보 보호책임자
 *   9. 안전성 확보 조치
 *   10. 개인정보처리방침의 변경
 *
 * 공식 문의 이메일: privacy@taxcube.kr
 *   - 회사 정보(주소·사업자번호 등)는 COMPANY 상수에서 import
 *   - 단, 개인정보 문의용 이메일은 본 페이지에 직접 지정
 *     (constants.ts 의 contactEmail 은 일반 기술 문의용이라 사용 안 함)
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COMPANY } from '@/config/constants';
import { Header } from '@/components/common/Header';
import { Footer } from '@/components/common/Footer';

const h2Style: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  marginTop: 36,
  marginBottom: 12,
  color: '#0B1829',
};

const h3Style: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  marginTop: 20,
  marginBottom: 8,
  color: '#0B1829',
};

const noteStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '12px 16px',
  background: '#F5F7FA',
  borderLeft: '3px solid #0F5FCC',
  borderRadius: 6,
  fontSize: 13.5,
  color: '#475569',
  lineHeight: 1.65,
};

/** 본 페이지 한정 — 개인정보 공식 문의 이메일 */
const PRIVACY_EMAIL = 'privacy@taxcube.kr';

export function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const effectiveDate = '2026년 5월 1일';

  return (
    <div className="landing-root">
      {/* 헤더/푸터의 B2B 모달 핸들러는 약관 페이지에서 의미 없으므로
          모달 열기는 메인 페이지로 돌려보내는 동작으로 처리 */}
      <Header onOpenB2B={() => { window.location.href = '/'; }} />

      <main
        style={{
          background: '#fff',
          color: '#222',
          minHeight: '60vh',
        }}
      >
        <div
          style={{
            maxWidth: 880,
            margin: '0 auto',
            padding: '60px 24px',
            lineHeight: 1.8,
            fontSize: 15,
          }}
        >
          <header style={{ marginBottom: 40, paddingBottom: 24, borderBottom: '2px solid #0B1829' }}>
            <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8 }}>개인정보처리방침</h1>
            <p style={{ color: '#666', fontSize: 14 }}>
              {COMPANY.brandKo} ({COMPANY.brandEn}) · 개인정보처리자: {COMPANY.legalNameKo}
            </p>
            <p style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              시행일: {effectiveDate}
            </p>
          </header>

          <p>
            {COMPANY.legalNameKo}(이하 "회사", 사업자등록번호: {COMPANY.businessNumber})는
            "{COMPANY.brandKo}" 브랜드로 제공하는 서비스와 관련하여 「개인정보 보호법」 및
            관련 법령을 준수하며, 정보주체의 개인정보를 적법하고 안전하게 처리하기 위해
            본 개인정보처리방침을 수립·공개합니다.
          </p>

          {/* ---------------- 제1조 ---------------- */}
          <h2 style={h2Style}>제1조 (개인정보의 처리 목적)</h2>
          <p>회사는 다음의 목적을 위해서만 개인정보를 처리하며, 처리 목적이 변경되는 경우에는 사전 동의를 받습니다.</p>
          <p>
            1. <strong>상담 서비스 제공</strong>: 상담 신청 접수, 회신, 컨설팅 진행에 필요한 본인 확인 및 연락
            <br />
            2. <strong>견적 및 계약 진행</strong>: 컨설팅 견적 산출, 정식 용역 계약 체결을 위한 기업 정보 확인
            <br />
            3. <strong>서비스 운영 및 분쟁 처리</strong>: 민원 처리, 분쟁 조정, 법적 의무 이행
          </p>

          {/* ---------------- 제2조 ---------------- */}
          <h2 style={h2Style}>제2조 (수집하는 개인정보 항목 및 수집 방법)</h2>
          <h3 style={h3Style}>1) 수집 항목</h3>
          <p>
            <strong>가. 필수 항목</strong>: 회사명, 담당자명(또는 대표자명), 연락처(휴대전화), 이메일
            <br />
            <strong>나. 선택 항목</strong>: 사업자등록번호, 직원수, 업종, 매출 정보, 문의 내용, 첨부 서류,
            영업 접촉 경로(B2B 접수 시)
            <br />
            <strong>다. 자동 수집 항목</strong>: 접속 IP 주소, 접속 일시, 브라우저 정보, 쿠키, 서비스 이용 기록
          </p>
          <h3 style={h3Style}>2) 수집 방법</h3>
          <p>
            ① 본 사이트의 상담 신청 폼 및 B2B 접수 모달을 통한 정보주체의 직접 입력
            <br />
            ② 서비스 이용 과정에서 자동 생성·수집되는 정보 (Firebase Hosting 등 인프라)
          </p>
          <div style={noteStyle}>
            ※ 회사는 사상·신념, 노동조합·정당의 가입·탈퇴, 정치적 견해, 건강 및 성생활에 관한 정보 등
            민감정보는 처리하지 않습니다.
          </div>

          {/* ---------------- 제3조 ---------------- */}
          <h2 style={h2Style}>제3조 (개인정보의 처리 및 보유 기간)</h2>
          <p>
            회사는 법령에 따른 보관 기간 또는 정보주체로부터 동의받은 보관 기간 내에서
            개인정보를 처리·보유합니다.
          </p>
          <p>
            1. <strong>상담 신청 정보</strong>: 상담 완료 후 <strong>3년</strong> 또는 동의 철회 시까지
            <br />
            2. <strong>계약 체결 시 정보</strong>: 「전자상거래 등에서의 소비자보호에 관한 법률」 및
            「국세기본법」 등 관계 법령에 따라 보관 (계약·청약철회 등의 기록 5년, 대금결제·재화공급 기록 5년)
            <br />
            3. <strong>접속 기록</strong>: 「통신비밀보호법」에 따라 3개월 보관
          </p>
          <p>보관 기간이 경과한 개인정보는 지체 없이 파기합니다.</p>

          {/* ---------------- 제4조 ---------------- */}
          <h2 style={h2Style}>제4조 (개인정보의 제3자 제공)</h2>
          <p>
            회사는 정보주체의 개인정보를 본 방침에서 명시한 범위 내에서만 처리하며,
            정보주체의 동의, 법률의 특별한 규정 등 「개인정보 보호법」 제17조 및 제18조에 해당하는 경우 외에는
            제3자에게 제공하지 않습니다.
          </p>

          {/* ---------------- 제5조 ---------------- */}
          <h2 style={h2Style}>제5조 (개인정보 처리의 위탁)</h2>
          <p>회사는 원활한 서비스 제공을 위해 다음과 같이 개인정보 처리 업무를 위탁할 수 있습니다.</p>
          <p>
            1. <strong>클라우드 인프라 및 데이터 저장</strong>: Google LLC (Firebase / Google Cloud) —
            데이터 호스팅, 저장, 인증
            <br />
            2. <strong>전문 업무 수행 시 제휴 기관</strong>: 세무 신고는 제휴 세무사사무소,
            회계 감사는 제휴 회계법인, 법률 자문은 제휴 법률사무소를 통해 진행될 수 있으며,
            이 경우 위탁 사실을 사전에 안내합니다.
          </p>
          <div style={noteStyle}>
            ※ 회사는 위탁 계약 체결 시 「개인정보 보호법」 제26조에 따라 위탁업무 수행 목적 외
            개인정보 처리 금지, 기술적·관리적 보호조치, 재위탁 제한 등을 계약서에 명시하고,
            수탁자가 안전하게 개인정보를 처리하는지 감독합니다.
          </div>

          {/* ---------------- 제6조 ---------------- */}
          <h2 style={h2Style}>제6조 (정보주체의 권리·의무 및 행사 방법)</h2>
          <p>정보주체는 회사에 대해 언제든지 다음과 같은 권리를 행사할 수 있습니다.</p>
          <p>
            1. 개인정보 열람 요구
            <br />
            2. 오류 등이 있을 경우 정정 요구
            <br />
            3. 삭제 요구
            <br />
            4. 처리정지 요구
            <br />
            5. 동의 철회
          </p>
          <p>
            권리 행사는 회사에 대해 서면, 전자우편 등을 통하여 하실 수 있으며, 회사는 이에 대해
            지체 없이 조치합니다. 문의 이메일:{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} style={{ color: '#0F5FCC' }}>{PRIVACY_EMAIL}</a>
          </p>

          {/* ---------------- 제7조 ---------------- */}
          <h2 style={h2Style}>제7조 (개인정보의 파기)</h2>
          <p>
            회사는 개인정보 보유 기간의 경과, 처리 목적 달성 등 개인정보가 불필요하게 되었을 때에는
            지체 없이 해당 개인정보를 파기합니다.
          </p>
          <p>
            1. <strong>파기 절차</strong>: 파기 사유가 발생한 개인정보를 선정하여
            개인정보 보호책임자의 승인을 받아 파기합니다.
            <br />
            2. <strong>파기 방법</strong>:
            <br />
            — 전자적 파일: 기록을 재생할 수 없는 방법으로 영구 삭제
            <br />
            — 종이 문서: 분쇄기로 분쇄하거나 소각
          </p>

          {/* ---------------- 제8조 ---------------- */}
          <h2 style={h2Style}>제8조 (개인정보 보호책임자)</h2>
          <p>
            회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의
            불만 처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.
          </p>
          <p>
            ■ <strong>개인정보 보호책임자</strong>
            <br />
            — 회사: {COMPANY.legalNameKo}
            <br />
            — 대표이사: {COMPANY.ceo}
            <br />
            — 이메일:{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} style={{ color: '#0F5FCC' }}>{PRIVACY_EMAIL}</a>
          </p>

          {/* ---------------- 제9조 ---------------- */}
          <h2 style={h2Style}>제9조 (개인정보의 안전성 확보 조치)</h2>
          <p>
            회사는 「개인정보 보호법」 제29조에 따라 다음과 같은 안전성 확보에 필요한
            기술적·관리적 조치를 하고 있습니다.
          </p>
          <p>
            1. <strong>관리적 조치</strong>: 내부관리계획 수립·시행, 정기적 직원 교육, 접근 권한 최소화
            <br />
            2. <strong>기술적 조치</strong>: 개인정보처리시스템 접근 권한 관리, 접근 통제 시스템 설치,
            고유식별정보 등의 암호화, 보안 프로그램 설치, 접속 기록의 보관 및 위·변조 방지
            <br />
            3. <strong>물리적 조치</strong>: 전산실, 자료보관실 등의 접근 통제
          </p>

          {/* ---------------- 제10조 ---------------- */}
          <h2 style={h2Style}>제10조 (개인정보처리방침의 변경)</h2>
          <p>
            본 방침은 {effectiveDate}부터 시행됩니다.
            <br />
            법령·정책 또는 회사 내부 방침 변경에 따라 본 방침을 수정할 수 있으며, 변경 시에는
            적용 7일 전 본 사이트에 공지합니다.
          </p>

          <div style={{ ...noteStyle, marginTop: 40 }}>
            ※ {COMPANY.legalNameKo}는 컨설팅 서비스 제공자로서 직접 세무대리, 회계 감사,
            법률 사무, 변리 사무 등 자격이 필요한 업무를 수행하지 않으며,
            해당 업무는 제휴 전문기관이 별도의 계약을 통해 수행합니다.
            본 방침은 회사가 처리하는 개인정보에 한해 적용되며,
            제휴 전문기관이 별도로 수집·처리하는 개인정보에는 해당 기관의 개인정보처리방침이 적용됩니다.
          </div>

          {/* ---------------- 푸터 정보 ---------------- */}
          <footer
            style={{
              marginTop: 60,
              paddingTop: 24,
              borderTop: '1px solid #ddd',
              fontSize: 13,
              color: '#666',
              textAlign: 'center',
            }}
          >
            {COMPANY.legalNameKo} · 사업자등록번호: {COMPANY.businessNumber}
            <br />
            {COMPANY.address} {COMPANY.addressDetail}
            <br />
            <br />
            <Link to="/" style={{ color: '#0F5FCC' }}>← 홈으로 돌아가기</Link>
            <span style={{ color: '#ccc', margin: '0 12px' }}>|</span>
            <Link to="/terms" style={{ color: '#0F5FCC' }}>이용약관</Link>
          </footer>
        </div>
      </main>

      <Footer />
    </div>
  );
}
